import { useState, useEffect, useCallback } from 'react';
import { API, formatWeight, formatDateTime } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Eye, Calendar, FileText, ShoppingCart, Filter, X } from 'lucide-react';

const DAYS_OPTIONS = [
  { label: '1 Week', value: 7 },
  { label: '2 Weeks', value: 14 },
  { label: '1 Month', value: 30 },
  { label: '3 Months', value: 90 },
  { label: '6 Months', value: 180 },
  { label: '1 Year', value: 365 },
];

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', mobile_numbers: '', address: '', shop_name: '' });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [detailLoading, setDetailLoading] = useState(false);
  // New filters
  const [salesRanges, setSalesRanges] = useState([]);
  const [selectedSalesRange, setSelectedSalesRange] = useState('');
  const [notVisitedDays, setNotVisitedDays] = useState('');
  const [customDays, setCustomDays] = useState('');
  const [showCustomDays, setShowCustomDays] = useState(false);

  // Fetch sales ranges once
  useEffect(() => {
    API.get('/customers/stats/sales-ranges').then(res => setSalesRanges(res.data)).catch(() => {});
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const daysVal = showCustomDays ? parseInt(customDays) : parseInt(notVisitedDays);
      if (daysVal > 0) params.not_visited_days = daysVal;
      if (selectedSalesRange) {
        const range = salesRanges.find(r => r.label === selectedSalesRange);
        if (range) {
          params.min_sales_g = range.min;
          params.max_sales_g = range.max;
        }
      }
      const res = await API.get('/customers', { params });
      setCustomers(res.data);
    } catch(e) { console.error(e); }
  }, [search, notVisitedDays, customDays, showCustomDays, selectedSalesRange, salesRanges]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await API.post('/customers', {
        name: form.name, mobile_numbers: form.mobile_numbers.split(',').map(s => s.trim()).filter(Boolean),
        address: form.address, shop_name: form.shop_name,
      });
      toast.success('Customer created'); setShowCreate(false);
      setForm({ name: '', mobile_numbers: '', address: '', shop_name: '' }); fetchCustomers();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewDetail = async (cust, startDate = '', endDate = '') => {
    setDetailLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await API.get(`/customers/${cust.id}`, { params });
      setSelectedCustomer(res.data);
    } catch(e) { setSelectedCustomer(cust); }
    finally { setDetailLoading(false); }
  };

  const applyDateFilter = () => {
    if (selectedCustomer) {
      viewDetail(selectedCustomer, dateFilter.start, dateFilter.end);
    }
  };

  const clearDateFilter = () => {
    setDateFilter({ start: '', end: '' });
    if (selectedCustomer) viewDetail(selectedCustomer);
  };

  const clearAllFilters = () => {
    setSelectedSalesRange('');
    setNotVisitedDays('');
    setCustomDays('');
    setShowCustomDays(false);
  };

  const hasActiveFilters = selectedSalesRange || notVisitedDays || (showCustomDays && customDays);

  return (
    <div data-testid="customers-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div><h1>Customers</h1><p className="hidden sm:block">Manage customer records and transaction history</p></div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild><Button className="w-full sm:w-auto" data-testid="customer-create-button"><Plus size={16} className="mr-2" />New Customer</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Customer</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><label className="text-sm font-medium mb-1 block">Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="customer-name-input" /></div>
              <div><label className="text-sm font-medium mb-1 block">Mobile(s) *</label><Input value={form.mobile_numbers} onChange={e => setForm({...form, mobile_numbers: e.target.value})} placeholder="9876543210, 9876543211" required data-testid="customer-mobile-input" /></div>
              <div><label className="text-sm font-medium mb-1 block">Address</label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Shop Name</label><Input value={form.shop_name} onChange={e => setForm({...form, shop_name: e.target.value})} /></div>
              <Button type="submit" className="w-full" data-testid="customer-create-submit">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search by name, mobile, shop..." value={search} onChange={e => setSearch(e.target.value)} data-testid="customer-search-input" />
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
          <Filter size={14} />
          <span>Filters:</span>
        </div>
        {/* Sales Quantity Range */}
        <Select value={selectedSalesRange} onValueChange={setSelectedSalesRange}>
          <SelectTrigger className="w-full sm:w-44 h-8 text-xs" data-testid="customer-sales-range-filter">
            <SelectValue placeholder="Sales Quantity" />
          </SelectTrigger>
          <SelectContent>
            {salesRanges.map(r => (
              <SelectItem key={r.label} value={r.label} className="text-xs">
                {r.label} ({r.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Not Visited Since */}
        {!showCustomDays ? (
          <Select value={notVisitedDays} onValueChange={(v) => {
            if (v === 'custom') {
              setShowCustomDays(true);
              setNotVisitedDays('');
            } else {
              setNotVisitedDays(v);
            }
          }}>
            <SelectTrigger className="w-full sm:w-48 h-8 text-xs" data-testid="customer-not-visited-filter">
              <SelectValue placeholder="Not Visited Since" />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OPTIONS.map(o => (
                <SelectItem key={o.value} value={String(o.value)} className="text-xs">{o.label}</SelectItem>
              ))}
              <SelectItem value="custom" className="text-xs font-semibold">Custom days...</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div className="flex items-center gap-1">
            <Input
              type="number" min="1" placeholder="Days"
              value={customDays} onChange={e => setCustomDays(e.target.value)}
              className="w-20 h-8 text-xs" data-testid="customer-custom-days-input"
            />
            <span className="text-xs text-muted-foreground">days</span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setShowCustomDays(false); setCustomDays(''); }}>
              <X size={12} />
            </Button>
          </div>
        )}

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-8" data-testid="customer-clear-filters">
            <X size={12} className="mr-1" />Clear
          </Button>
        )}

        <span className="text-xs text-muted-foreground sm:ml-auto">{customers.length} customers</span>
      </div>

      {/* Mobile: Card list */}
      <div className="mobile-card-list">
        {customers.map(c => (
          <Card key={c.id} className="cursor-pointer" onClick={() => viewDetail(c)} data-testid={`customer-card-${c.id}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm">{c.name}</p>
                {c.total_sales_g > 0 && <span className="text-xs font-mono text-amber-400">{formatWeight(c.total_sales_g)}</span>}
              </div>
              <p className="font-mono text-xs text-muted-foreground">{(c.mobile_numbers || []).join(', ')}</p>
              {c.shop_name && <p className="text-xs text-muted-foreground mt-1">{c.shop_name}</p>}
              {c.days_since_visit != null && (
                <p className="text-[10px] text-muted-foreground mt-1">Last visit: {c.days_since_visit === 0 ? 'Today' : `${c.days_since_visit}d ago`}</p>
              )}
            </CardContent>
          </Card>
        ))}
        {customers.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No customers</p>}
      </div>

      {/* Desktop: Table */}
      <div className="desktop-table">
        <Card>
          <CardContent className="p-0">
            <div className="table-scroll-wrapper">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Shop</TableHead>
                    <TableHead className="text-right">Total Sales</TableHead>
                    <TableHead>Last Visit</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="font-semibold">{c.name}</TableCell>
                      <TableCell className="font-mono">{(c.mobile_numbers || []).join(', ')}</TableCell>
                      <TableCell>{c.shop_name || '\u2014'}</TableCell>
                      <TableCell className="text-right font-mono text-amber-400">{c.total_sales_g > 0 ? formatWeight(c.total_sales_g) : '\u2014'}</TableCell>
                      <TableCell className="text-xs">
                        {c.days_since_visit != null
                          ? c.days_since_visit === 0 ? 'Today' : `${c.days_since_visit}d ago`
                          : '\u2014'}
                      </TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => viewDetail(c)} data-testid={`customer-view-${c.id}`}><Eye size={16} /></Button></TableCell>
                    </TableRow>
                  ))}
                  {customers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No customers</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedCustomer} onOpenChange={() => { setSelectedCustomer(null); setDateFilter({ start: '', end: '' }); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedCustomer?.name}</DialogTitle></DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><span className="text-muted-foreground">Mobile:</span><br /><b className="font-mono">{(selectedCustomer.mobile_numbers || []).join(', ')}</b></div>
                <div><span className="text-muted-foreground">Shop:</span><br />{selectedCustomer.shop_name || '\u2014'}</div>
                <div><span className="text-muted-foreground">Address:</span><br />{selectedCustomer.address || '\u2014'}</div>
                <div><span className="text-muted-foreground">Since:</span><br />{formatDateTime(selectedCustomer.created_at)}</div>
              </div>

              {/* Date filter */}
              <div className="flex flex-col sm:flex-row gap-2 items-end">
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">From</label>
                  <Input type="date" value={dateFilter.start} onChange={e => setDateFilter({...dateFilter, start: e.target.value})} className="text-xs" data-testid="customer-date-from" />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">To</label>
                  <Input type="date" value={dateFilter.end} onChange={e => setDateFilter({...dateFilter, end: e.target.value})} className="text-xs" data-testid="customer-date-to" />
                </div>
                <Button size="sm" onClick={applyDateFilter} className="text-xs" data-testid="customer-date-apply"><Calendar size={12} className="mr-1" />Filter</Button>
                {(dateFilter.start || dateFilter.end) && <Button size="sm" variant="outline" onClick={clearDateFilter} className="text-xs">Clear</Button>}
              </div>

              <Tabs defaultValue="dealings">
                <TabsList className="grid grid-cols-2 w-full sm:w-auto">
                  <TabsTrigger value="dealings" className="text-xs"><FileText size={12} className="mr-1" />Bills ({(selectedCustomer.dealings || []).length})</TabsTrigger>
                  <TabsTrigger value="operations" className="text-xs"><ShoppingCart size={12} className="mr-1" />Operations ({(selectedCustomer.operations || []).length})</TabsTrigger>
                </TabsList>
                <TabsContent value="dealings">
                  <div className="table-scroll-wrapper">
                    <Table className="data-table">
                      <TableHeader><TableRow><TableHead>Bill #</TableHead><TableHead>Type</TableHead><TableHead>Gold (g)</TableHead><TableHead>Salesman</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {(selectedCustomer.dealings || []).map(d => (
                          <TableRow key={d.id}>
                            <TableCell className="font-mono text-xs">{d.bill_number}</TableCell>
                            <TableCell><Badge variant={d.bill_type === 'sale_return' ? 'destructive' : 'default'} className="text-xs">{d.bill_type || 'sale'}</Badge></TableCell>
                            <TableCell className="font-mono text-xs">{formatWeight(d.total_gold_g || 0)}</TableCell>
                            <TableCell className="text-xs">{d.salesman_name}</TableCell>
                            <TableCell className="text-xs">{formatDateTime(d.created_at)}</TableCell>
                          </TableRow>
                        ))}
                        {(selectedCustomer.dealings || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-muted-foreground text-xs">No dealings found</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                <TabsContent value="operations">
                  <div className="table-scroll-wrapper">
                    <Table className="data-table">
                      <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Box</TableHead><TableHead>Gold (g)</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {(selectedCustomer.operations || []).map(op => (
                          <TableRow key={op.id}>
                            <TableCell className="text-xs">{formatDateTime(op.created_at)}</TableCell>
                            <TableCell><Badge variant={op.operation_type === 'sale' ? 'destructive' : 'default'} className="text-xs">{op.operation_type.replace(/_/g, ' ')}</Badge></TableCell>
                            <TableCell className="font-mono text-xs">{op.box_id}</TableCell>
                            <TableCell className="font-mono text-xs">{formatWeight(op.gold_weight_g)}</TableCell>
                            <TableCell className="text-xs">{op.submitted_by}</TableCell>
                          </TableRow>
                        ))}
                        {(selectedCustomer.operations || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-muted-foreground text-xs">No operations found</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
